import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

const CATEGORIES = [
  { key: 'credit',        label: 'Kredit',        icon: '💳', color: '#ef4444' },
  { key: 'country',       label: 'Länderrisiko',  icon: '🌍', color: '#f97316' },
  { key: 'operational',   label: 'Operationell',  icon: '⚙', color: '#f59e0b' },
  { key: 'legal',         label: 'Rechtlich',     icon: '⚖', color: '#8b5cf6' },
  { key: 'market',        label: 'Markt',         icon: '📈', color: '#3b82f6' },
  { key: 'fx',            label: 'Währung',       icon: '💱', color: '#06b6d4' },
  { key: 'reputational',  label: 'Reputation',    icon: '🏢', color: '#ec4899' },
];

const STATUSES = [
  { key: 'open',      label: 'Offen',       color: '#ef4444' },
  { key: 'mitigated', label: 'Mitigiert',   color: '#f59e0b' },
  { key: 'accepted',  label: 'Akzeptiert',  color: '#3b82f6' },
  { key: 'closed',    label: 'Geschlossen', color: '#22c55e' },
];

const RISK_LEVELS = [
  { key: 'low',      label: 'Niedrig',   color: '#22c55e', min: 1,  max: 4  },
  { key: 'medium',   label: 'Mittel',    color: '#f59e0b', min: 5,  max: 9  },
  { key: 'high',     label: 'Hoch',      color: '#f97316', min: 10, max: 16 },
  { key: 'critical', label: 'Kritisch',  color: '#ef4444', min: 17, max: 25 },
];

function riskLevel(score) {
  return RISK_LEVELS.find(r => score >= r.min && score <= r.max) || RISK_LEVELS[0];
}

function catMeta(k) { return CATEGORIES.find(c => c.key === k) || CATEGORIES[0]; }
function statusMeta(k) { return STATUSES.find(s => s.key === k) || STATUSES[0]; }

function ScoreMatrix({ likelihood, impact, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {[['Eintrittswahrscheinlichkeit', 'likelihood', likelihood], ['Schadensausmaß', 'impact', impact]].map(([label, key, val]) => (
        <div key={key} className="form-group">
          <label className="form-label">{label} (1–5)</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1,2,3,4,5].map(n => {
              const score = key === 'likelihood' ? n * impact : likelihood * n;
              const lvl   = riskLevel(score);
              return (
                <button key={n} onClick={() => onChange(key, n)}
                  style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                    background: n === val ? lvl.color : 'var(--bg-surface)',
                    color: n === val ? '#000' : 'var(--text3)',
                  }}>
                  {n}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ gridColumn: '1 / -1' }}>
        {(() => {
          const score = likelihood * impact;
          const lvl   = riskLevel(score);
          return (
            <div style={{ padding: '8px 14px', borderRadius: 8, background: lvl.color + '22', border: `1px solid ${lvl.color}44`,
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: lvl.color }}>{score}</span>
              <span style={{ fontWeight: 700, color: lvl.color }}>{lvl.label}</span>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>({likelihood} × {impact})</span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function RiskModal({ risk, onClose, onSaved }) {
  const [form, setForm] = useState(risk ? { ...risk } : {
    category: 'credit', title: '', description: '', likelihood: 3, impact: 3,
    mitigation_action: '', owner: '', due_date: '', deal_reference: '',
    counterparty_ref: '', status: 'open', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setErr('Titel erforderlich'); return; }
    setSaving(true); setErr('');
    try {
      if (risk) await API.put(`/risks/${risk.id}`, form);
      else      await API.post('/risks', form);
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{risk ? 'Risiko bearbeiten' : 'Neues Risiko erfassen'}</div>
        {err && <div className="error-msg">{err}</div>}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Kategorie</label>
            <select className="form-input" value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Risikotitel *</label>
          <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="z.B. Zahlungsausfall Counterparty XYZ" />
        </div>

        <div className="form-group">
          <label className="form-label">Beschreibung</label>
          <textarea className="form-input" rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
        </div>

        <ScoreMatrix likelihood={form.likelihood} impact={form.impact}
          onChange={(k, v) => set(k, v)} />

        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Mitigationsmaßnahmen</label>
          <textarea className="form-input" rows={3} value={form.mitigation_action} onChange={e => set('mitigation_action', e.target.value)}
            placeholder="Welche Maßnahmen reduzieren das Risiko?" />
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Verantwortlicher</label>
            <input className="form-input" value={form.owner} onChange={e => set('owner', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Fälligkeitsdatum</label>
            <input type="date" className="form-input" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Deal-Referenz</label>
            <input className="form-input" value={form.deal_reference} onChange={e => set('deal_reference', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Counterparty-Referenz</label>
          <input className="form-input" value={form.counterparty_ref} onChange={e => set('counterparty_ref', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RiskRegisterPage() {
  const [risks, setRisks]           = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [q, setQ]                   = useState('');
  const [catFilter, setCat]         = useState('');
  const [statusFilter, setStatus]   = useState('open');
  const [modal, setModal]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (catFilter)    params.category = catFilter;
      if (statusFilter) params.status   = statusFilter;
      if (q)            params.q        = q;
      const [r, s] = await Promise.all([
        API.get('/risks', { params }),
        API.get('/risks/summary'),
      ]);
      setRisks(r.data);
      setSummary(s.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, catFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (risk) => {
    if (!window.confirm(`"${risk.title}" wirklich löschen?`)) return;
    await API.delete(`/risks/${risk.id}`);
    load();
  };

  return (
    <div className="page-content">
      {modal && (
        <RiskModal risk={modal === 'create' ? null : modal}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>⚠ Risk Register</h1>
          <p>Risikomanagement — Kredit-, Länder-, Markt- und Operationelle Risiken</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ Risiko erfassen</button>
      </div>

      {/* Summary */}
      {summary && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { label: 'Offen gesamt', value: summary.total_open, color: 'var(--text1)' },
            ...RISK_LEVELS.map(l => ({
              label: l.label, value: summary.by_level?.[l.key] || 0, color: l.color,
            })),
          ].map(c => (
            <div key={c.label} className="card" style={{ minWidth: 110, textAlign: 'center', padding: '12px 16px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ maxWidth: 240 }} placeholder="Suche…"
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 180 }} value={catFilter} onChange={e => setCat(e.target.value)}>
          <option value="">Alle Kategorien</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
        </select>
        <select className="form-input" style={{ maxWidth: 180 }} value={statusFilter} onChange={e => setStatus(e.target.value)}>
          <option value="">Alle Status</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>⟳</button>
      </div>

      {loading && <div className="loading">Lade…</div>}

      {!loading && risks.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Keine Risiken erfasst</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {risks.map(r => {
          const lvl = riskLevel(r.risk_score);
          const cat = catMeta(r.category);
          const st  = statusMeta(r.status);
          return (
            <div key={r.id} className="card" style={{ borderLeft: `4px solid ${lvl.color}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ fontSize: 28, marginTop: 2 }}>{cat.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{r.title}</span>
                    <span style={{ padding: '1px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: lvl.color + '22', color: lvl.color }}>
                      Score {r.risk_score} — {lvl.label}
                    </span>
                    <span style={{ padding: '1px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: st.color + '22', color: st.color }}>
                      {st.label}
                    </span>
                    <span style={{ padding: '1px 8px', borderRadius: 4, fontSize: 11,
                      background: cat.color + '22', color: cat.color }}>
                      {cat.label}
                    </span>
                  </div>
                  {r.description && (
                    <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>{r.description}</div>
                  )}
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
                    <span>W: {r.likelihood} × A: {r.impact}</span>
                    {r.owner && <span>Owner: {r.owner}</span>}
                    {r.due_date && <span>Fällig: {r.due_date}</span>}
                    {r.deal_reference && <span>Deal: {r.deal_reference}</span>}
                  </div>
                  {r.mitigation_action && (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: 'var(--bg-surface)', borderRadius: 6,
                      fontSize: 12, color: 'var(--text2)' }}>
                      🛡 {r.mitigation_action}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModal(r)}>✏</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r)}>🗑</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
